/**
 * ViewCustomizeBar (iter1073) — лентата „Подредете изгледа".
 * Включва режима, в който всяка секция получава бутон „Скрий", показва колко
 * са скрити и връща всичко с едно натискане.
 */
import React, { useContext } from "react";
import { Eye, EyeOff, RotateCcw, Check } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";

/** Изборът на потребителя се подава и през контекст, защото част от
 *  секциите се рендерират от помощни компоненти извън страницата. */
export const ViewPrefsContext = React.createContext(null);

export const ViewCustomizeBar = ({ isDark, editMode, setEditMode, hiddenCount,
                                   reset, testid = "view-customize-bar" }) => (
  <div
    data-testid={testid}
    className={`mb-3 flex flex-wrap items-center gap-2 rounded-2xl border px-3 py-2 ${
      isDark ? "bg-slate-900/50 border-slate-700" : "bg-white border-slate-200"}`}
  >
    <button
      type="button"
      onClick={() => setEditMode(!editMode)}
      data-testid={`${testid}-toggle`}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors ${
        editMode
          ? "bg-emerald-500 text-white"
          : isDark ? "bg-slate-800 text-slate-200 hover:bg-slate-700"
                   : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
    >
      {editMode ? <Check className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      {editMode ? "Готово" : "Подредете изгледа"}
    </button>

    <span className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}
      data-testid={`${testid}-count`}>
      {hiddenCount > 0
        ? `Скрити секции: ${hiddenCount}`
        : "Всички секции се показват"}
    </span>

    {hiddenCount > 0 && (
      <button
        type="button"
        onClick={reset}
        data-testid={`${testid}-reset`}
        className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold text-cyan-500 hover:underline"
      >
        <RotateCcw className="w-3.5 h-3.5" /> Върнете всичко
      </button>
    )}
  </div>
);

/** Обвивка на една секция: скрива я или я маркира за скриване. */
export const HideableSection = ({ id, label, prefs, isDark, children, style }) => {
  const ctxPrefs = useContext(ViewPrefsContext);
  const theme = useTheme();
  const active = prefs || ctxPrefs;
  const dark = isDark ?? theme?.isDark;
  if (!active) return <div style={style}>{children}</div>;
  const { isHidden, toggle, editMode } = active;
  const off = isHidden(id);

  if (off && !editMode) return null;

  if (off && editMode) {
    return (
      <div
        style={style}
        data-testid={`hidden-stub-${id}`}
        className={`mb-3 flex items-center justify-between gap-2 rounded-2xl border border-dashed px-3 py-2.5 ${
          dark ? "border-slate-700 text-slate-400" : "border-slate-300 text-slate-500"}`}
      >
        <span className="text-[11px] font-semibold truncate">{label || id}</span>
        <button
          type="button"
          onClick={() => toggle(id)}
          data-testid={`unhide-${id}`}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-bold text-white"
        >
          <Eye className="w-3 h-3" /> Покажете
        </button>
      </div>
    );
  }

  return (
    <div style={style} className="relative" data-testid={`hideable-${id}`}>
      {editMode && (
        <button
          type="button"
          onClick={() => toggle(id)}
          data-testid={`hide-${id}`}
          className="absolute right-2 top-2 z-20 inline-flex items-center gap-1 rounded-full bg-rose-500 px-2.5 py-1 text-[10px] font-bold text-white shadow"
        >
          <EyeOff className="w-3 h-3" /> Скрийте
        </button>
      )}
      {children}
    </div>
  );
};

export default ViewCustomizeBar;
